import React, { useEffect, useState } from 'react';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import { prefetchAlbumImage, useAlbumGallery } from '../../hooks/useAlbumGallery';

const INITIAL_EAGER_IMAGES = 6;
const LIGHTBOX_PREFETCH_DISTANCE = 1;

const imageStyle = {
  width: '100%',
  height: 'auto',
  display: 'block',
};

const skeletonStyle = {
  width: '100%',
  borderRadius: '8px',
  background:
    'linear-gradient(135deg, rgba(235, 226, 220, 0.9) 0%, rgba(250, 246, 240, 0.95) 50%, rgba(226, 214, 203, 0.9) 100%)',
};

const noticeStyle = {
  padding: '16px 20px',
  marginBottom: '24px',
  borderRadius: '12px',
  backgroundColor: '#f6efe7',
  color: '#5a4637',
  fontSize: '15px',
};

const emptyStateStyle = {
  textAlign: 'center',
  padding: '48px 16px',
  color: '#6b5b4e',
};

const lightboxSlideFrameStyle = {
  position: 'relative',
  display: 'inline-block',
  maxWidth: '100%',
  maxHeight: '100%',
  lineHeight: 0,
};

const lightboxSlideImageStyle = {
  display: 'block',
  maxWidth: '100%',
  maxHeight: 'calc(100vh - 48px)',
  objectFit: 'contain',
};

const lightboxActionStackStyle = {
  position: 'absolute',
  top: '18px',
  right: '18px',
  zIndex: 2,
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  alignItems: 'flex-end',
};

const lightboxDownloadActionStyle = {
  position: 'absolute',
  right: '18px',
  bottom: '18px',
  zIndex: 2,
};

const hdToggleButtonStyle = {
  appearance: 'none',
  background: 'rgba(14, 14, 14, 0.62)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.75)',
  borderRadius: '999px',
  color: '#ffffff',
  cursor: 'pointer',
  fontSize: '11px',
  fontWeight: 800,
  letterSpacing: '0.14em',
  lineHeight: 1,
  minWidth: '58px',
  padding: '10px 13px',
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.34), inset 0 1px 0 rgba(255, 255, 255, 0.14)',
  textShadow: '0 1px 2px rgba(0, 0, 0, 0.35)',
  textTransform: 'uppercase',
  transition:
    'background-color 180ms ease, border-color 180ms ease, box-shadow 180ms ease, color 180ms ease, transform 180ms ease',
};

const downloadButtonStyle = {
  ...hdToggleButtonStyle,
  minWidth: '46px',
  width: '46px',
  height: '46px',
  padding: '0',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const hdToggleActiveStyle = {
  background: '#f2c14b',
  borderColor: '#ffe19a',
  boxShadow:
    '0 0 0 1px rgba(255, 225, 154, 0.42), 0 0 18px rgba(242, 193, 75, 0.55), 0 12px 32px rgba(0, 0, 0, 0.3)',
  color: '#241507',
  textShadow: 'none',
  transform: 'translateY(-1px)',
};

const hdToggleDisabledStyle = {
  background: 'rgba(20, 20, 20, 0.42)',
  borderColor: 'rgba(255, 255, 255, 0.32)',
  boxShadow: '0 8px 22px rgba(0, 0, 0, 0.2)',
  color: 'rgba(255, 255, 255, 0.62)',
  cursor: 'not-allowed',
};

const buildLightboxSlides = (slides, currentIndex, isHdActive) =>
  slides.map((slide, index) => ({
    ...slide,
    src: isHdActive && index === currentIndex && slide.hdSrc ? slide.hdSrc : slide.src,
  }));

const getDownloadFileName = (slide) => {
  const keyFileName = slide?.key?.split('/').pop();

  if (keyFileName) {
    return keyFileName;
  }

  try {
    const url = new URL(slide?.hdSrc || '');
    const urlFileName = url.pathname.split('/').pop();
    return urlFileName || 'photo-hd.jpg';
  } catch {
    return 'photo-hd.jpg';
  }
};

const PortfolioSectionS2 = (props) => {
  const { error, hasMore, loading, sentinelRef, slides, visibleImages } = useAlbumGallery();
  const [open, setOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHdActive, setIsHdActive] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const currentSlide = slides[currentIndex];
  const isCurrentSlideHdAvailable = Boolean(currentSlide?.hdSrc && currentSlide.hdSrc !== currentSlide.src);
  const lightboxSlides = buildLightboxSlides(slides, currentIndex, isHdActive);

  useEffect(() => {
    if (!open || slides.length === 0) {
      return;
    }

    for (let offset = -LIGHTBOX_PREFETCH_DISTANCE; offset <= LIGHTBOX_PREFETCH_DISTANCE; offset += 1) {
      const nextSlide = slides[currentIndex + offset];

      if (nextSlide?.src) {
        prefetchAlbumImage(nextSlide.src);
      }
    }
  }, [currentIndex, open, slides]);

  useEffect(() => {
    if (!open || !isCurrentSlideHdAvailable) {
      return;
    }

    prefetchAlbumImage(currentSlide.hdSrc);
  }, [currentSlide, isCurrentSlideHdAvailable, open]);

  useEffect(() => {
    setIsHdActive(false);
  }, [currentIndex, open]);

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setIsHdActive(false);
    setOpen(true);
  };

  const closeLightbox = () => {
    setOpen(false);
    setIsHdActive(false);
    setIsDownloading(false);
  };

  const downloadHdPhoto = async (slide) => {
    if (!slide?.hdSrc || isDownloading) {
      return;
    }

    setIsDownloading(true);

    try {
      const response = await fetch(slide.hdSrc, { mode: 'cors' });

      if (!response.ok) {
        throw new Error(`Download request failed with status ${response.status}`);
      }

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const downloadLink = document.createElement('a');

      downloadLink.href = objectUrl;
      downloadLink.download = getDownloadFileName(slide);
      downloadLink.rel = 'noopener';
      downloadLink.style.display = 'none';
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      window.setTimeout(() => {
        window.URL.revokeObjectURL(objectUrl);
      }, 1000);
    } catch (error) {
      window.open(slide.hdSrc, '_blank', 'noopener,noreferrer');
    } finally {
      setIsDownloading(false);
    }
  };

  const renderLightboxActions = () => {
    return (
      <div style={lightboxActionStackStyle}>
        <button
          type="button"
          aria-label={
            isCurrentSlideHdAvailable
              ? isHdActive
                ? 'Disattiva modalita HD'
                : 'Attiva modalita HD'
              : 'Versione HD non disponibile'
          }
          aria-pressed={isCurrentSlideHdAvailable ? isHdActive : undefined}
          disabled={!isCurrentSlideHdAvailable}
          onClick={() => setIsHdActive((currentValue) => !currentValue)}
          style={{
            ...hdToggleButtonStyle,
            ...(isCurrentSlideHdAvailable ? null : hdToggleDisabledStyle),
            ...(isCurrentSlideHdAvailable && isHdActive ? hdToggleActiveStyle : null),
          }}
          title={isCurrentSlideHdAvailable ? 'Attiva o disattiva la versione Full HD' : 'Versione HD non disponibile'}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <span
              aria-hidden="true"
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: isCurrentSlideHdAvailable && isHdActive ? '#241507' : 'rgba(255, 255, 255, 0.9)',
                boxShadow: isCurrentSlideHdAvailable && isHdActive ? '0 0 10px rgba(36, 21, 7, 0.35)' : '0 0 10px rgba(255, 255, 255, 0.3)',
              }}
            />
            HD
          </span>
        </button>
      </div>
    );
  };

  const renderLightboxSlide = ({ slide, rect }) => {
    const maxWidth = Math.max(rect.width - 24, 0);
    const maxHeight = Math.max(rect.height - 24, 0);

    return (
        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ ...lightboxSlideFrameStyle, maxWidth, maxHeight }}>
          <img src={slide.src} alt={slide.alt || ''} style={lightboxSlideImageStyle} draggable={false} />
          {renderLightboxActions()}
          <button
            type="button"
            aria-label={
              isCurrentSlideHdAvailable
                ? isDownloading
                  ? 'Download della foto in alta qualita in corso'
                  : 'Scarica foto in alta qualita'
                : 'Download HD non disponibile'
            }
            disabled={!isCurrentSlideHdAvailable || isDownloading}
            onClick={() => downloadHdPhoto(currentSlide)}
            style={{
              ...lightboxDownloadActionStyle,
              ...downloadButtonStyle,
              ...(!isCurrentSlideHdAvailable || isDownloading ? hdToggleDisabledStyle : null),
            }}
            title={
              isCurrentSlideHdAvailable
                ? isDownloading
                  ? 'Download in corso'
                  : 'Scarica la foto in alta qualita'
                : 'Download HD non disponibile'
            }
          >
            {isDownloading ? (
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
            ) : (
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 3v11" />
                <path d="m7 11 5 5 5-5" />
                <path d="M5 21h14" />
              </svg>
            )}
          </button>
        </div>
      </div>
    );
  };

  return (
    <section className={`wpo-portfolio-section-s2 section-padding ${props.prClass}`} id="gallery">
      <div className="container-fluid">
        <div className="sortable-gallery">
          <div className="gallery-filters"></div>
          <div className="row">
            <div className="col-lg-12">
              {error && <div style={noticeStyle}>{error}</div>}
              <div className="portfolio-grids gallery-container clearfix">
                {loading ? (
                  <ResponsiveMasonry columnsCountBreakPoints={{ 350: 1, 750: 2, 900: 3 }}>
                    <Masonry columnsCount={4} gutter="30px">
                      {Array.from({ length: 8 }).map((_, index) => (
                        <div className="grid" key={`album-skeleton-${index}`}>
                          <div style={{ ...skeletonStyle, aspectRatio: index % 2 === 0 ? '4 / 5' : '3 / 4' }} />
                        </div>
                      ))}
                    </Masonry>
                  </ResponsiveMasonry>
                ) : visibleImages.length > 0 ? (
                  <>
                    <ResponsiveMasonry columnsCountBreakPoints={{ 350: 1, 750: 2, 900: 3 }}>
                      <Masonry columnsCount={4} gutter="30px">
                        {visibleImages.map((image, index) => (
                          <div className="grid" key={image.key}>
                            <div
                              className="img-holder"
                              onClick={() => openLightbox(index)}
                              style={{ cursor: 'pointer' }}
                            >
                              <img
                                src={image.thumbSrc}
                                alt={image.alt}
                                className="img img-responsive"
                                decoding="async"
                                fetchPriority={index < 2 ? 'high' : 'auto'}
                                loading={index < INITIAL_EAGER_IMAGES ? 'eager' : 'lazy'}
                                style={imageStyle}
                                onError={(event) => {
                                  if (event.currentTarget.src !== image.src) {
                                    event.currentTarget.src = image.src;
                                  }
                                }}
                              />
                              <div className="hover-content">
                                <i className="ti-plus"></i>
                              </div>
                            </div>
                          </div>
                        ))}
                      </Masonry>
                    </ResponsiveMasonry>
                    {hasMore && <div ref={sentinelRef} style={{ height: '1px' }} aria-hidden="true" />}
                  </>
                ) : (
                  <div style={emptyStateStyle}>
                    <h3>Nessuna foto disponibile</h3>
                    <p>Pubblica il manifest dell&apos;album per mostrare le immagini della galleria.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Lightbox
        open={open}
        close={closeLightbox}
        slides={lightboxSlides}
        index={currentIndex}
        controller={{ closeOnBackdropClick: true }}
        render={{ slide: renderLightboxSlide }}
        on={{ view: ({ index }) => setCurrentIndex(index) }}
      />
    </section>
  );
};

export default PortfolioSectionS2;
